/**
 * Revenue Intelligence Engine
 * Computes job economics, source performance, and revenue alerts.
 */
import type { Job } from "./types";

// ── Job Economics ──

export interface JobEconomics {
  jobId: string;
  customerName: string;
  leadSource: string;
  salesRep: string;
  contractValue: number;
  materialCost: number;
  installCost: number;
  incentiveCost: number;
  otherExpenseCost: number;
  marketingCost: number;
  netProfit: number;
  margin: number; // 0-1
  stage: string;
}

function toPositiveNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function toCoreMaterialLineTotal(row: NonNullable<Job["materialsList"]>[number]): number {
  const qty = Math.max(0, toPositiveNumber(row?.quantityRequired));
  const unit = toPositiveNumber(row?.unitCost);
  const taxPercent = Math.max(0, toPositiveNumber(row?.taxPercent));
  return qty * unit * (1 + (taxPercent / 100));
}

function toFieldMaterialLineSubtotal(row: NonNullable<Job["fieldMaterials"]>[number]): number {
  const qty = Math.max(1, toPositiveNumber(row?.quantity) || 1);
  const unit = toPositiveNumber(row?.unitCost);
  return qty * unit;
}

function deriveFieldMaterialReceiptTax(job: Job): number {
  const seen = new Set<string>();
  let totalTax = 0;

  for (const material of job.fieldMaterials ?? []) {
    for (const receipt of material.receipts ?? []) {
      const attachmentId = String(receipt?.attachmentId ?? "").trim();
      const fileName = String(receipt?.fileName ?? "").trim().toLowerCase();
      const vendorName = String(receipt?.vendorName ?? "").trim().toLowerCase();
      const purchasedAt = String(receipt?.purchasedAt ?? "").trim();
      const total = typeof receipt?.total === "number" && Number.isFinite(receipt.total)
        ? receipt.total.toFixed(2)
        : "";
      const tax = typeof receipt?.tax === "number" && Number.isFinite(receipt.tax)
        ? receipt.tax
        : 0;

      const identity = attachmentId || [fileName || "no-file", vendorName || "no-vendor", purchasedAt || "no-date", total || "no-total"].join(":");
      if (!identity || seen.has(identity)) continue;
      seen.add(identity);
      totalTax += Math.max(0, tax);
    }
  }

  return totalTax;
}

function deriveMaterialCost(job: Job, contractValue: number): number {
  const listBased = (job.materialsList ?? []).reduce((sum, row) => {
    return sum + toCoreMaterialLineTotal(row);
  }, 0);

  const fieldBased = (job.fieldMaterials ?? []).reduce((sum, row) => {
    return sum + toFieldMaterialLineSubtotal(row);
  }, 0);

  const combinedItemized = listBased + fieldBased + deriveFieldMaterialReceiptTax(job);
  if (combinedItemized > 0) return combinedItemized;

  const explicit = toPositiveNumber(job.financials?.estimatedMaterialCost);
  if (explicit > 0) return explicit;

  const margin = toPositiveNumber(job.financials?.marginEstimate);
  const labor = toPositiveNumber(job.financials?.estimatedLaborCost);
  const sub = toPositiveNumber(job.financials?.subcontractorPayout);
  if (contractValue > 0 && margin > 0 && (labor + sub) > 0) {
    return Math.max(0, contractValue - (labor + sub) - margin);
  }

  return 0;
}

function deriveInstallCost(job: Job, contractValue: number, materialCost: number): number {
  const labor = toPositiveNumber(job.financials?.estimatedLaborCost);
  const sub = toPositiveNumber(job.financials?.subcontractorPayout);
  const explicit = labor + sub;
  if (explicit > 0) return explicit;

  const margin = toPositiveNumber(job.financials?.marginEstimate);
  if (contractValue > 0 && margin > 0) {
    const derived = contractValue - materialCost - margin;
    if (derived > 0) return derived;
  }

  const payoutBased = toPositiveNumber(job.payoutAmount);
  const isSubcontractorJob = job.assignmentType === "Subcontractor";
  if (isSubcontractorJob && payoutBased > 0) {
    // Only trust payout fallback when it behaves like a subcontractor install cost.
    // Prevents install cost from incorrectly mirroring total contract value.
    if (contractValue <= 0 || payoutBased < (contractValue * 0.98)) return payoutBased;
  }

  return 0;
}

function deriveIncentiveCost(job: Job): number {
  return (job.offerCompensationConfig?.incentives ?? []).reduce((sum, item) => {
    const amount = toPositiveNumber(item?.amount);
    return sum + amount;
  }, 0);
}

function deriveOtherExpenseCost(job: Job): number {
  return (job.financials?.otherExpenses ?? []).reduce((sum, row) => {
    const amount = toPositiveNumber(row?.amount);
    return sum + amount;
  }, 0);
}

export function computeJobEconomics(job: Job): JobEconomics {
  const cv = toPositiveNumber(job.financials?.contractValue) || toPositiveNumber(job.payoutAmount);
  const mat = deriveMaterialCost(job, cv);
  const baseInstallCost = deriveInstallCost(job, cv, mat);
  const incentiveCost = deriveIncentiveCost(job);
  const otherExpenseCost = deriveOtherExpenseCost(job);
  const installCost = baseInstallCost + incentiveCost + otherExpenseCost;
  const mktCost = job.marketing?.leadCost ?? 0;
  const net = cv - mat - installCost - mktCost;
  return {
    jobId: job.id,
    customerName: job.customerName,
    leadSource: job.marketing?.leadSource ?? "Unknown",
    salesRep: job.salesRep || "Unassigned",
    contractValue: cv,
    materialCost: mat,
    installCost,
    incentiveCost,
    otherExpenseCost,
    marketingCost: mktCost,
    netProfit: net,
    margin: cv > 0 ? net / cv : 0,
    stage: job.currentStage,
  };
}

// ── Source Performance ──

export interface SourcePerformance {
  source: string;
  totalJobs: number;
  totalRevenue: number;
  totalProfit: number;
  totalLeadCost: number;
  avgContractValue: number;
  avgMargin: number;
  cac: number; // cost per acquisition
}

export function aggregateBySource(jobs: Job[]): SourcePerformance[] {
  const map = new Map<string, JobEconomics[]>();
  for (const j of jobs) {
    const e = computeJobEconomics(j);
    const src = e.leadSource;
    if (!map.has(src)) map.set(src, []);
    map.get(src)!.push(e);
  }
  const results: SourcePerformance[] = [];
  for (const [source, econs] of map) {
    const totalRevenue = econs.reduce((s, e) => s + e.contractValue, 0);
    const totalProfit = econs.reduce((s, e) => s + e.netProfit, 0);
    const totalLeadCost = econs.reduce((s, e) => s + e.marketingCost, 0);
    const totalJobs = econs.length;
    results.push({
      source,
      totalJobs,
      totalRevenue,
      totalProfit,
      totalLeadCost,
      avgContractValue: totalJobs > 0 ? totalRevenue / totalJobs : 0,
      avgMargin: totalJobs > 0 ? econs.reduce((s, e) => s + e.margin, 0) / totalJobs : 0,
      cac: totalJobs > 0 ? totalLeadCost / totalJobs : 0,
    });
  }
  return results.sort((a, b) => b.totalRevenue - a.totalRevenue);
}

// ── Rep Performance ──

export interface RepPerformance {
  rep: string;
  totalJobs: number;
  totalRevenue: number;
  totalProfit: number;
  avgMargin: number;
  avgContractValue: number;
}

export function aggregateByRep(jobs: Job[]): RepPerformance[] {
  const map = new Map<string, JobEconomics[]>();
  for (const j of jobs) {
    const e = computeJobEconomics(j);
    if (!map.has(e.salesRep)) map.set(e.salesRep, []);
    map.get(e.salesRep)!.push(e);
  }
  const results: RepPerformance[] = [];
  for (const [rep, econs] of map) {
    const totalRevenue = econs.reduce((s, e) => s + e.contractValue, 0);
    const totalProfit = econs.reduce((s, e) => s + e.netProfit, 0);
    results.push({
      rep,
      totalJobs: econs.length,
      totalRevenue,
      totalProfit,
      avgMargin: econs.length > 0 ? econs.reduce((s, e) => s + e.margin, 0) / econs.length : 0,
      avgContractValue: econs.length > 0 ? totalRevenue / econs.length : 0,
    });
  }
  return results.sort((a, b) => b.totalRevenue - a.totalRevenue);
}

// ── Revenue Alerts ──

export interface RevenueAlert {
  id: string;
  severity: "critical" | "warning" | "info";
  message: string;
  jobIds: string[];
  suggestedAction: string;
}

export function generateRevenueAlerts(
  jobs: Job[],
  marginThreshold = 0.20,
): RevenueAlert[] {
  const alerts: RevenueAlert[] = [];
  const econs = jobs.map(computeJobEconomics);

  // Low margin jobs
  const lowMargin = econs.filter(e => e.contractValue > 0 && e.margin < marginThreshold);
  if (lowMargin.length > 0) {
    alerts.push({
      id: "rev-low-margin",
      severity: lowMargin.some(e => e.margin < 0.10) ? "critical" : "warning",
      message: `${lowMargin.length} job(s) below ${(marginThreshold * 100).toFixed(0)}% margin threshold`,
      jobIds: lowMargin.map(e => e.jobId),
      suggestedAction: "Review pricing and costs on these jobs",
    });
  }

  // CAC > profit (per source)
  const sources = aggregateBySource(jobs);
  const highCAC = sources.filter(s => s.cac > 0 && s.cac > s.totalProfit / Math.max(s.totalJobs, 1));
  if (highCAC.length > 0) {
    alerts.push({
      id: "rev-high-cac",
      severity: "warning",
      message: `${highCAC.length} source(s) have CAC exceeding per-job profit: ${highCAC.map(s => s.source).join(", ")}`,
      jobIds: [],
      suggestedAction: "Evaluate marketing spend on underperforming sources",
    });
  }

  // Unpaid deposits on scheduled jobs
  const unpaidDeposits = jobs.filter(j => {
    if (!j.scheduledInstallDate) return false;
    const lineItems = j.financials?.paymentPlan?.lineItems ?? [];
    return lineItems.some(li => li.kind === "deposit" && li.status !== "paid");
  });
  if (unpaidDeposits.length > 0) {
    alerts.push({
      id: "rev-unpaid-deposits",
      severity: "critical",
      message: `${unpaidDeposits.length} scheduled job(s) with unpaid deposits`,
      jobIds: unpaidDeposits.map(j => j.id),
      suggestedAction: "Collect deposits before install dates",
    });
  }

  // Negative profit jobs
  const negative = econs.filter(e => e.netProfit < 0);
  if (negative.length > 0) {
    alerts.push({
      id: "rev-negative-profit",
      severity: "critical",
      message: `${negative.length} job(s) are losing money`,
      jobIds: negative.map(e => e.jobId),
      suggestedAction: "Investigate cost overruns immediately",
    });
  }

  return alerts;
}

// ── Summary Metrics ──

export interface RevenueSummary {
  totalRevenue: number;
  totalProfit: number;
  avgMargin: number;
  totalMarketingCost: number;
  atRiskRevenue: number;
  jobCount: number;
}

export function computeRevenueSummary(jobs: Job[]): RevenueSummary {
  const econs = jobs.map(computeJobEconomics);
  const totalRevenue = econs.reduce((s, e) => s + e.contractValue, 0);
  const totalProfit = econs.reduce((s, e) => s + e.netProfit, 0);
  const totalMarketingCost = econs.reduce((s, e) => s + e.marketingCost, 0);
  const atRisk = econs.filter(e => e.margin < 0.15).reduce((s, e) => s + e.contractValue, 0);
  return {
    totalRevenue,
    totalProfit,
    avgMargin: econs.length > 0 ? econs.reduce((s, e) => s + e.margin, 0) / econs.length : 0,
    totalMarketingCost,
    atRiskRevenue: atRisk,
    jobCount: econs.length,
  };
}
